import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import surToggleOverViewPdp from "@tCentro/Handlers/CENPDPOverViewBlockHandler";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    showDescription = "showDescription",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDescription]: boolean;
    };
}
const mapDispatchToProps = {
    surToggleOverViewPdp,
};
type Props = WidgetProps & HasProduct & OwnProps & ResolveThunks<typeof mapDispatchToProps>;
const tabCss: FlattenSimpleInterpolation = css`
    background: ${(props: TabProps) =>
        props.selected ? "linear-gradient(0deg,rgba(38,107,64,0.08),rgba(38,107,64,0.08)),#FFFFFF;" : "transparent"};
    color: ${(props: TabProps) => (props.selected ? getColor("primary") : " #807f80;")};
    font-weight: ${(props: TabProps) => (props.selected ? "600" : "normal")};
    border-radius: ${(props: TabProps) => (props.selected ? "40px;" : "none")};
    border: none;
    outline: ${(props: TabProps) => props.selected && "none"};
    padding: 8px 16px;
    margin-right: 10px;
    z-index: 0;
    &:hover {
        cursor: pointer;
        border: none;
    }
    &:focus {
        outline: none !important;
    }
` as FlattenSimpleInterpolation;

const tabContent = css`
    background: ${COLORS.white};
    border: none;
    padding-left: 0;
`;

const tabGroup = css`
    padding: 0;
    z-index: 0;
`;

export interface ProductDetailsSpecificationsStyles {
    attributeTypeValuesText?: TypographyPresentationProps;
    tabHeading: InjectableCss;
    tabContainer?: InjectableCss;
    linkStyles?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    tabHeading: {
        css: css`
            z-index: 0 !important;
            &[aria-selected="false"] {
                span {
                    color: #807f80;
                    font-size: ${TYPOGRAPHY.size.base};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 24px;
                }
            }
        `,
    },
    attributeTypeValuesText: {
        css: css`
            body {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 28px;
            }
        `,
    },
    tabContainer: {
        css: css`
            background: #ffffff;
            width: 100%;
            height: 100%;
            padding: 40px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 40px 10px;
                    `,
                    css`
                        padding: 40px 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
            >div[class^="TabGroupWrapper"] {
                div[role="tabPanel"] {
                    padding-bottom: 0px;
                }
            }
        `,
    },
    linkStyles: {
        css: css`
            margin-top: 10px;
            margin-bottom: 10px;
            display: block;
            &:focus {
                outline-color: transparent !important;
            }
            > span {
                color: ${getProp("theme.typography.p.color")} !important;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
};

const styles = specificationsStyles;

const CENProductDetailsTab: React.FC<Props> = ({
    product: { content, properties, specifications },
    fields: { showDescription },
    surToggleOverViewPdp,
}) => {
    const description = content?.htmlContent ? content?.htmlContent : properties.brandHtmlContent;
    if (!description) {
        return null;
    }

    const renderingDescription = description.split("</head>");
    const contentLength = renderingDescription?.length > 1 ? renderingDescription[1].length : description?.length;

    const [status, setStatus] = useState(false);
    const toggleStatus = () => {
        setStatus(!status);
        surToggleOverViewPdp();
    };

    if (!renderingDescription) {
        return null;
    }

    const productTabs = [];
    const descriptionTab =
        renderingDescription && showDescription ? (
            <StyledWrapper key="Overview">
                <StyledWrapper>
                    <Typography {...styles.attributeTypeValuesText}>
                        {status || getIsWebCrawler()
                            ? parse(transformContentHtml(description), parserOptions)
                            : parse(transformContentHtml(truncate(description, { length: 220 })), parserOptions)}
                        {contentLength > 220 && !getIsWebCrawler() && (
                            <Link href="" onClick={toggleStatus} {...styles.linkStyles}>
                                {status ? "Show Less" : "Show More"}
                            </Link>
                        )}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        ) : null;

    productTabs.push(descriptionTab);

    if (specifications && specifications.length > 0) {
        specifications.forEach((item, index) => {
            if (item.htmlContent && item.htmlContent.trim() !== "") {
                const spec = item.htmlContent.split("</head>");
                const specLength = spec?.length > 1 ? spec[1].length : item.htmlContent.length;

                const specificationsTab = (
                    <StyledWrapper key={item.name}>
                        <Typography {...styles.attributeTypeValuesText}>
                            {status || getIsWebCrawler()
                                ? parse(transformContentHtml(item.htmlContent), parserOptions)
                                : parse(
                                      transformContentHtml(truncate(item.htmlContent, { length: 220 })),
                                      parserOptions,
                                  )}
                            {specLength > 220 && !getIsWebCrawler() && (
                                <Link href="" onClick={toggleStatus} {...styles.linkStyles}>
                                    {status ? "Show Less" : "Show More"}
                                </Link>
                            )}
                        </Typography>
                    </StyledWrapper>
                );
                productTabs.push(specificationsTab);
            }
        });
    }

    const tabs: any = [];
    productTabs.forEach(productTab => {
        if (productTab) {
            tabs.push(productTab);
        }
    });

    return (
        <>
            {tabs && tabs?.length > 0 ? (
                <StyledWrapper {...styles.tabContainer}>
                    <TabGroup cssOverrides={{ tabContent, tabGroup }} {...styles.tabHeading}>
                        {tabs?.map((tabItem: any, index: any) => {
                            return (
                                <Tab key={tabItem?.key} css={tabCss} headline={tabItem?.key} tabKey={`tab${index}`}>
                                    {tabItem}
                                </Tab>
                            );
                        })}
                    </TabGroup>
                </StyledWrapper>
            ) : null}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(null, mapDispatchToProps)(CENProductDetailsTab)),
    definition: {
        displayName: "CEN Product Details tab",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: fields.showDescription,
                displayName: "Show Description",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
